import { useState } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Avatar,
  Grid, Divider, Alert, Chip, InputAdornment, IconButton, CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon, Email as EmailIcon, Phone as PhoneIcon,
  Badge as BadgeIcon, Lock as LockIcon, Save as SaveIcon,
  Visibility, VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import API from '../../BaseURL';
import swal from '../../utils/swal';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();

  const [profileForm, setProfileForm] = useState({
    first_name: user?.full_name?.split(' ')[0] || '',
    last_name:  user?.full_name?.split(' ').slice(1).join(' ') || '',
    telephone:  user?.phone || '',
  });
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState({ current: false, next: false, confirm: false });
  const [pwMsg, setPwMsg]   = useState({ type: '', text: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const saveProfile = async () => {
    setProfileSaving(true); setProfileMsg({ type: '', text: '' });
    try {
      await API.put('/auth/profile', profileForm);
      await refreshUser();
      swal.success('Profile updated');
    } catch (e) {
      swal.error(swal.pickError(e, 'Update failed'));
    } finally { setProfileSaving(false); }
  };

  const changePassword = async () => {
    setPwMsg({ type: '', text: '' });
    if (!pwForm.current || !pwForm.next) { setPwMsg({ type: 'error', text: 'Fill all fields' }); return; }
    if (pwForm.next.length < 6)         { setPwMsg({ type: 'error', text: 'Password must be 6+ characters' }); return; }
    if (pwForm.next !== pwForm.confirm) { setPwMsg({ type: 'error', text: 'New passwords do not match' }); return; }
    setPwSaving(true);
    try {
      await API.post('/auth/change-password', {
        current_password: pwForm.current,
        new_password:     pwForm.next,
      });
      setPwForm({ current: '', next: '', confirm: '' });
      swal.success('Password changed — please use the new password next time you sign in');
    } catch (e) {
      setPwMsg({ type: 'error', text: swal.pickError(e, 'Could not change password') });
    } finally { setPwSaving(false); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
        <Avatar sx={{
          width: 88, height: 88, fontSize: 32, fontWeight: 800,
          bgcolor: 'primary.main', boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
        }}>
          {user?.full_name?.[0]?.toUpperCase()}
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight={800}>{user?.full_name}</Typography>
          <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={user?.role_label || user?.role || 'No role'} color="primary" variant="outlined" />
            {user?.department && (
              <Chip size="small" label={user.department} variant="outlined" />
            )}
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Account Details</Typography>
              </Box>
              <Divider sx={{ mb: 2.5 }} />
              {profileMsg.text && (
                <Alert severity={profileMsg.type} sx={{ mb: 2, borderRadius: 2 }}
                  onClose={() => setProfileMsg({ type: '', text: '' })}>
                  {profileMsg.text}
                </Alert>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="First Name" value={profileForm.first_name}
                    onChange={(e) => setProfileForm({ ...profileForm, first_name: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Last Name" value={profileForm.last_name}
                    onChange={(e) => setProfileForm({ ...profileForm, last_name: e.target.value })} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth disabled label="Email" value={user?.email || ''}
                    InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Phone" value={profileForm.telephone}
                    onChange={(e) => setProfileForm({ ...profileForm, telephone: e.target.value })}
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment> }} />
                </Grid>
                <Grid item xs={12}>
                  <Button variant="contained" startIcon={profileSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={saveProfile} disabled={profileSaving}>
                    Save Changes
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <BadgeIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Role & Access</Typography>
              </Box>
              <Divider sx={{ mb: 2.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Role</Typography>
                <Typography variant="body2" fontWeight={700}>{user?.role_label || user?.role || '—'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">Department</Typography>
                <Typography variant="body2" fontWeight={700}>{user?.department || '—'}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Pages assigned</Typography>
                <Typography variant="body2" fontWeight={700}>
                  {user?.role === 'super_admin' ? 'All' : (user?.pages?.length || 0)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <LockIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>Change Password</Typography>
              </Box>
              <Divider sx={{ mb: 2.5 }} />
              {pwMsg.text && (
                <Alert severity={pwMsg.type} sx={{ mb: 2, borderRadius: 2 }}
                  onClose={() => setPwMsg({ type: '', text: '' })}>
                  {pwMsg.text}
                </Alert>
              )}
              {[
                { key: 'current', label: 'Current Password' },
                { key: 'next',    label: 'New Password' },
                { key: 'confirm', label: 'Confirm New Password' },
              ].map(({ key, label }) => (
                <TextField key={key} fullWidth size="small" sx={{ mb: 1.5 }} label={label}
                  type={showPw[key] ? 'text' : 'password'}
                  value={pwForm[key]}
                  onChange={(e) => setPwForm({ ...pwForm, [key]: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPw({ ...showPw, [key]: !showPw[key] })}>
                          {showPw[key] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ))}
              <Button variant="contained" fullWidth startIcon={pwSaving ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
                onClick={changePassword} disabled={pwSaving} sx={{ mt: 1 }}>
                Update Password
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
